<template>
	<div>
		<b-card no-body class="mb-0">
			<div class="m-2">
				<b-row align-v="center">
					<b-col md="6">
						<h4 class="mb-0">{{ catalogLabel }}</h4>
						<small class="text-muted">
							Gestione los registros del catálogo. Los registros desactivados no se mostrarán en los selectores, pero los datos históricos se mantienen.
						</small>
					</b-col>
					<b-col md="6" class="text-right mt-1 mt-md-0">
						<b-button
							variant="outline-secondary"
							class="mr-1"
							@click="goBack"
						>
							<feather-icon icon="ArrowLeftIcon" class="mr-50" />
							<span>Volver</span>
						</b-button>
						<b-button variant="primary" @click="openAddModal">
							<feather-icon icon="PlusIcon" class="mr-50" />
							<span>Agregar</span>
						</b-button>
					</b-col>
				</b-row>

				<b-row class="mt-2">
					<b-col md="4">
						<label class="d-block">Filtrar por estado</label>
						<v-select
							v-model="statusFilter"
							:options="statusOptions"
							:clearable="false"
							@input="fetchRecords"
						/>
					</b-col>
					<b-col v-if="hasDepartment" md="4">
						<label class="d-block">Filtrar por departamento</label>
						<v-select
							v-model="departmentFilter"
							:options="departmentOptions"
							placeholder="Todos"
							@input="fetchRecords"
						/>
					</b-col>
				</b-row>
			</div>

			<b-table
				class="position-relative"
				:items="records"
				:fields="tableFields"
				:busy="isLoading"
				responsive
				show-empty
				empty-text="No se encontraron registros"
				bordered
			>
				<template #table-busy>
					<div class="text-center text-primary my-2">
						<b-spinner class="align-middle mb-1"></b-spinner>
						<div>
							<b>CARGANDO</b>
						</div>
					</div>
				</template>

				<template #cell(index)="data">
					{{ data.index + 1 }}
				</template>

				<template #cell(name)="data">
					<strong>{{ data.item.name }}</strong>
				</template>

				<template #cell(department_name)="data">
					<span>{{ data.item.department_name || '-' }}</span>
				</template>

				<template #cell(status)="data">
					<b-badge :variant="data.item.status ? 'light-success' : 'light-danger'" pill>
						{{ data.item.status ? 'Activo' : 'Inactivo' }}
					</b-badge>
				</template>

				<template #cell(updater_fullname)="data">
					<span>{{ data.item.updater_fullname || '-' }}</span>
				</template>

				<template #cell(updatedAt)="data">
					<span class="text-nowrap">{{ formatStringToDate(data.item.updatedAt) }}</span>
				</template>

				<template #cell(actions)="data">
					<b-button
						variant="flat-primary"
						class="btn-icon"
						size="sm"
						v-b-tooltip.hover
						title="Editar"
						@click="openEditModal(data.item)"
					>
						<feather-icon icon="EditIcon" />
					</b-button>
					<b-button
						:variant="`flat-${data.item.status ? 'danger' : 'success'}`"
						class="btn-icon"
						size="sm"
						v-b-tooltip.hover
						:title="data.item.status ? 'Desactivar' : 'Activar'"
						@click="confirmToggleStatus(data.item)"
					>
						<feather-icon :icon="data.item.status ? 'SlashIcon' : 'CheckCircleIcon'" />
					</b-button>
				</template>
			</b-table>
		</b-card>

		<!-- Modal Agregar/Editar -->
		<b-modal
			ref="refFormModal"
			:title="formMode === 'create' ? `Agregar ${singularLabel}` : `Editar ${singularLabel}`"
			title-tag="div"
			header-bg-variant="primary"
			header-text-variant="white"
			no-close-on-backdrop
			no-close-on-esc
			@hidden="resetForm"
		>
			<b-form-group label="Nombre" label-for="name-input">
				<b-form-input
					id="name-input"
					v-model="form.name"
					placeholder="Ingrese el nombre"
				/>
			</b-form-group>

			<b-form-group
				v-if="hasDepartment"
				label="Departamento"
				label-for="department-input"
			>
				<v-select
					id="department-input"
					v-model="form.department"
					:options="departmentOptions"
					placeholder="Seleccione departamento"
				/>
			</b-form-group>

			<template #modal-footer>
				<b-button variant="outline-secondary" @click="$refs.refFormModal.hide()">
					Cancelar
				</b-button>
				<b-button variant="primary" :disabled="isSaving" @click="submitForm">
					<b-spinner v-if="isSaving" small class="mr-50" />
					<span>Guardar</span>
				</b-button>
			</template>
		</b-modal>
	</div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from "@vue/composition-api"
import { VBTooltip } from "bootstrap-vue"
import { formatStringToDate } from "@core/utils/filter"
import VueSweetalert2 from "vue-sweetalert2"
import useNotifications from "@notifications"
import vSelect from "vue-select"
import masterDataStoreModule from "../masterDataStoreModule"
import store from "@/store"
import router from "@/router"
import axios from "@axios"
import Vue from "vue"
import "animate.css"

Vue.use(VueSweetalert2)

const CATALOG_META = {
	departments: { label: "Departamentos", singular: "Departamento", hasDepartment: false },
	positions: { label: "Cargos", singular: "Cargo", hasDepartment: true },
	locations: { label: "Ejecución de Servicio", singular: "Ejecución de Servicio", hasDepartment: false },
	"business-areas": { label: "Rubros", singular: "Rubro", hasDepartment: false }
}

export default {
	components: {
		vSelect
	},
	directives: {
		"b-tooltip": VBTooltip
	},
	setup(_, { root }) {
		const { swalNotification, toastNotification } = useNotifications()

		const STORE_NAME = "app-master-data"

		if (!store.hasModule(STORE_NAME))
			store.registerModule(STORE_NAME, masterDataStoreModule)

		onUnmounted(() => {
			if (store.hasModule(STORE_NAME))
				store.unregisterModule(STORE_NAME)
		})

		const catalog = computed(() => root.$route.params.catalog)
		const catalogMeta = computed(() => CATALOG_META[catalog.value] || { label: "", singular: "", hasDepartment: false })
		const catalogLabel = computed(() => catalogMeta.value.label)
		const singularLabel = computed(() => catalogMeta.value.singular)
		const hasDepartment = computed(() => catalogMeta.value.hasDepartment)

		const records = ref([])
		const isLoading = ref(false)
		const isSaving = ref(false)

		const statusOptions = [
			{ value: "", label: "Todos" },
			{ value: 1, label: "Activos" },
			{ value: 0, label: "Inactivos" }
		]
		const statusFilter = ref(statusOptions[0])

		const departmentOptions = ref([])
		const departmentFilter = ref(null)

		const tableFields = computed(() => {
			const base = [
				{ key: "index", label: "#", thClass: "text-center", tdClass: "text-center align-middle", thStyle: { width: "6%" } },
				{ key: "name", label: "Nombre", thClass: "text-center", tdClass: "align-middle" }
			]
			if (hasDepartment.value) {
				base.push({ key: "department_name", label: "Departamento", thClass: "text-center", tdClass: "align-middle" })
			}
			base.push(
				{ key: "status", label: "Estado", thClass: "text-center", tdClass: "text-center align-middle", thStyle: { width: "10%" } },
				{ key: "updater_fullname", label: "Último usuario", thClass: "text-center", tdClass: "text-center align-middle", thStyle: { width: "18%" } },
				{ key: "updatedAt", label: "Última actualización", thClass: "text-center", tdClass: "text-center align-middle", thStyle: { width: "15%" } },
				{ key: "actions", label: "Acciones", thClass: "text-center", tdClass: "text-center align-middle", thStyle: { width: "12%" } }
			)
			return base
		})

		const refFormModal = ref(null)
		const formMode = ref("create")
		const form = ref({ id: null, name: "", department: null })

		const resetForm = () => {
			form.value = { id: null, name: "", department: null }
			formMode.value = "create"
		}

		const fetchRecords = async () => {
			if (!catalog.value || !CATALOG_META[catalog.value]) {
				toastNotification("Catálogo no válido", false)
				return
			}
			isLoading.value = true
			try {
				const params = {}
				if (statusFilter.value.value !== "") params.status = statusFilter.value.value
				if (hasDepartment.value && departmentFilter.value) {
					params.department_id = departmentFilter.value.value
				}
				const response = await store.dispatch(`${STORE_NAME}/fetchRecords`, {
					catalog: catalog.value,
					params
				})
				records.value = response.data.items || []
			} catch (error) {
				toastNotification(error.response?.data?.message || "Error al cargar registros", false)
			} finally {
				isLoading.value = false
			}
		}

		const fetchDepartmentOptions = async () => {
			try {
				const { data } = await axios.get("/selectors/departments")
				departmentOptions.value = data.departments || []
			} catch (error) {
				toastNotification(error.response?.data?.message || "Error al cargar departamentos", false)
			}
		}

		const openAddModal = () => {
			resetForm()
			formMode.value = "create"
			refFormModal.value.show()
		}

		const openEditModal = (item) => {
			form.value = {
				id: item.id,
				name: item.name,
				department: hasDepartment.value && item.department_id
					? departmentOptions.value.find(d => d.value === item.department_id) || null
					: null
			}
			formMode.value = "edit"
			refFormModal.value.show()
		}

		const submitForm = async () => {
			if (!form.value.name || !form.value.name.trim()) {
				toastNotification("El nombre es requerido", false)
				return
			}
			if (hasDepartment.value && !form.value.department) {
				toastNotification("Seleccione un departamento", false)
				return
			}

			if (formMode.value === "edit") {
				const confirmed = await Vue.swal({
					title: "¿Está seguro(a) de guardar los cambios?",
					html: "<p>La información del registro será <b>modificada</b> y se reflejará en los listados de selección.</p><small>Los datos históricos que ya referencian a este registro se mantienen.</small>",
					icon: "warning",
					showCancelButton: true,
					confirmButtonText: "Sí, guardar",
					cancelButtonText: "Cancelar",
					customClass: {
						confirmButton: "btn btn-primary",
						cancelButton: "btn btn-outline-danger ml-1"
					},
					showClass: {
						popup: "animate__animated animate__tada"
					},
					buttonsStyling: false
				})
				if (!confirmed.value) return
			}

			isSaving.value = true
			try {
				const payload = { name: form.value.name.trim() }
				if (hasDepartment.value) payload.department_id = form.value.department.value

				if (formMode.value === "create") {
					const response = await store.dispatch(`${STORE_NAME}/createRecord`, {
						catalog: catalog.value,
						payload
					})
					swalNotification(response.data.message)
				} else {
					const response = await store.dispatch(`${STORE_NAME}/updateRecord`, {
						catalog: catalog.value,
						id: form.value.id,
						payload
					})
					swalNotification(response.data.message)
				}

				refFormModal.value.hide()
				await fetchRecords()
			} catch (error) {
				swalNotification(error.response?.data?.message || "Ocurrió un error al guardar", false)
			} finally {
				isSaving.value = false
			}
		}

		const confirmToggleStatus = (item) => {
			const activating = !item.status
			Vue.swal({
				title: activating
					? "¿Está seguro(a) de activar este registro?"
					: "¿Está seguro(a) de desactivar este registro?",
				html: activating
					? "<p>El registro <b>volverá a estar disponible</b> en los selectores.</p>"
					: "<p>El registro <b>no aparecerá en los selectores</b>, pero los datos históricos que lo referencian se mantienen.</p>",
				icon: activating ? "info" : "warning",
				showCancelButton: true,
				confirmButtonText: activating ? "Sí, activar" : "Sí, desactivar",
				cancelButtonText: "Cancelar",
				customClass: {
					confirmButton: "btn btn-primary",
					cancelButton: "btn btn-outline-danger ml-1"
				},
				showClass: {
					popup: "animate__animated animate__tada"
				},
				buttonsStyling: false
			}).then(async (result) => {
				if (!result.value) return
				try {
					const response = await store.dispatch(`${STORE_NAME}/toggleStatus`, {
						catalog: catalog.value,
						id: item.id
					})
					swalNotification(response.data.message)
					await fetchRecords()
				} catch (error) {
					swalNotification(error.response?.data?.message || "Error al cambiar el estado", false)
				}
			})
		}

		const goBack = () => {
			router.push({ name: "master-data-index" })
		}

		onMounted(async () => {
			if (hasDepartment.value) await fetchDepartmentOptions()
			await fetchRecords()
		})

		return {
			catalogLabel,
			singularLabel,
			hasDepartment,
			records,
			isLoading,
			isSaving,
			tableFields,
			statusOptions,
			statusFilter,
			departmentOptions,
			departmentFilter,

			refFormModal,
			formMode,
			form,

			fetchRecords,
			openAddModal,
			openEditModal,
			submitForm,
			confirmToggleStatus,
			resetForm,
			goBack,
			formatStringToDate
		}
	}
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
